import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ScoreBar, Trend } from "@/components/Scores";
import TodayBar from "@/components/TodayBar";
import { ArrowLeft, AlertTriangle, Plus, ClipboardList, Target, Trophy, HeartPulse } from "lucide-react";
import { toast } from "sonner";

const METRIC_META = {
  hrv_avg: { label: "HRV Avg", unit: " ms" },
  hrv_min: { label: "HRV Min", unit: " ms" },
  hrv_max: { label: "HRV Max", unit: " ms" },
  hr_avg: { label: "HR Avg", unit: " bpm" },
  hr_min: { label: "HR Min", unit: " bpm" },
  hr_max: { label: "HR Max", unit: " bpm" },
  resting_hr: { label: "Resting HR", unit: " bpm" },
  stress_avg: { label: "Stress Avg", unit: "" },
  stress_min: { label: "Stress Min", unit: "" },
  stress_max: { label: "Stress Max", unit: "" },
  bp_sys_avg: { label: "BP Sys Avg", unit: "" },
  bp_sys_min: { label: "BP Sys Min", unit: "" },
  bp_sys_max: { label: "BP Sys Max", unit: "" },
  bp_dia_avg: { label: "BP Dia Avg", unit: "" },
  bp_dia_min: { label: "BP Dia Min", unit: "" },
  bp_dia_max: { label: "BP Dia Max", unit: "" },
  steps: { label: "Steps", unit: "" },
  distance_km: { label: "Distance", unit: " km" },
  calories: { label: "Calories", unit: " kcal" },
  sleep_hours: { label: "Sleep", unit: " h" },
  sleep_deep_h: { label: "Deep Sleep", unit: " h" },
  sleep_light_h: { label: "Light Sleep", unit: " h" },
  sleep_rem_h: { label: "REM Sleep", unit: " h" },
};

function computeDev(latest, ref, higher) {
  if (latest == null || ref == null || ref === 0) return null;
  const pct = Math.round(((latest - ref) / ref) * 1000) / 10;
  const arrow = pct === 0 ? "flat" : latest > ref ? "up" : "down";
  const good = pct >= 0 === (higher !== false);
  return { pct, arrow, good };
}

export default function AthleteData({ athleteId: propId, embedded }) {
  const params = useParams();
  const id = propId || params.id;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [baseWin, setBaseWin] = useState("baseline");
  const canMedical = ["coach", "physio"].includes(user?.role);
  const isStaff = user?.role !== "athlete";
  const [inj, setInj] = useState({ body_part: "", severity: "mild", recovery_timeline_days: 14, notes: "" });
  const [asmt, setAsmt] = useState({ name: "", value: "", unit: "", date: "" });
  const [targets, setTargets] = useState({ snc: [], drills: [] });
  const [match, setMatch] = useState({ date: "", event: "", result: "", condition_note: "" });
  const [rtp, setRtp] = useState(null);

  const load = () => api.get(`/athletes/${id}`).then((r) => setA(r.data));
  const loadExtra = () => {
    api.get(`/athletes/${id}/targets`).then((r) => setTargets({ snc: r.data.targets?.snc || [], drills: r.data.targets?.drills || [] }));
    api.get(`/athletes/${id}/return-plan`).then((r) => setRtp(r.data));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); loadExtra(); }, [id]);

  const applyRtp = async () => {
    try { const r = await api.post(`/athletes/${id}/return-plan/apply`); toast.success(`Rehab plan applied (${r.data.applied} phases)`); loadExtra(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addTarget = (tc) => setTargets((p) => ({ ...p, [tc]: [...p[tc], { name: "", target: "" }] }));
  const setTargetField = (tc, i, f, v) => setTargets((p) => ({ ...p, [tc]: p[tc].map((t, idx) => idx === i ? { ...t, [f]: v } : t) }));
  const saveTargets = async (tc) => {
    await api.post(`/athletes/${id}/targets`, { task_class: tc, targets: targets[tc].filter((t) => t.name) });
    toast.success(`${tc.toUpperCase()} targets saved`);
  };
  const addMatch = async () => {
    if (!match.event || !match.date) return toast.error("Date and event required");
    await api.post(`/athletes/${id}/matches`, { ...match, stats: {} });
    toast.success("Match logged"); setMatch({ date: "", event: "", result: "", condition_note: "" });
  };

  const addInjury = async () => {
    if (!inj.body_part) return toast.error("Body part required");
    try {
      await api.post(`/athletes/${id}/injury`, { ...inj, recovery_timeline_days: Number(inj.recovery_timeline_days) });
      toast.success("Injury logged"); setInj({ body_part: "", severity: "mild", recovery_timeline_days: 14, notes: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const addAssessment = async () => {
    if (!asmt.name || asmt.value === "") return toast.error("Name and value required");
    try {
      await api.post(`/athletes/${id}/assessments`, { ...asmt, value: Number(asmt.value) });
      toast.success("Assessment logged"); setAsmt({ name: "", value: "", unit: "", date: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!a) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div>
      {!embedded && (
      <header className="h-16 border-b border-border flex items-center px-8 gap-4 sticky top-0 bg-background z-10">
        <button data-testid="back-btn" onClick={() => navigate("/")} className="text-muted-foreground hover:text-foreground transition-colors"><ArrowLeft className="w-5 h-5" /></button>
        <img src={a.avatar_url || `https://ui-avatars.com/api/?background=e5e5e5&color=0a0a0a&name=${encodeURIComponent(a.name)}`} alt="" className="w-9 h-9 grayscale object-cover border border-border" />
        <div>
          <h1 className="font-heading text-lg font-black tracking-tight leading-none">{a.name}</h1>
          <span className="text-xs text-muted-foreground uppercase tracking-wider">{a.sport} · {a.age}y</span>
        </div>
      </header>
      )}

      {!embedded && <TodayBar athleteId={id} />}

      <div className="grid lg:grid-cols-3 gap-px bg-border border-b border-border">
        <div className="lg:col-span-2 bg-background p-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-5">Trifit Scores</p>
          <div className="grid sm:grid-cols-2 gap-6">
            {["readiness", "sharpness", "effort", "rest"].map((m) => (
              <ScoreBar key={m} label={m} value={a.scores[m]} dev={a.status[m]} />
            ))}
          </div>
          <div className="mt-6 text-xs text-muted-foreground leading-relaxed">
            Readiness is S&C-based · Sharpness is skill-based · Effort & Rest are Trifit-derived. Green = above this athlete's own baseline, red = below.
          </div>
        </div>
        <div className="bg-background p-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4">Profile</p>
          <dl className="space-y-3 text-sm">
            <Row k="Level" v={a.level} /><Row k="Goal" v={a.goal} /><Row k="Location" v={a.location} />
            <Row k="Availability" v={a.availability?.replace("_", " ")} />
            <Row k="Next Event" v={a.next_event || "-"} />
          </dl>
        </div>
      </div>

      <div className="p-8 border-b border-border">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Latest vs L7 · L30 · All-time baseline · Median</p>
          <div className="flex items-center gap-2" data-testid="baseline-window-toggle">
            <span className="text-[10px] tracking-[0.15em] uppercase font-bold text-muted-foreground">Δ vs</span>
            {[["last_7", "L7"], ["l30", "L30"], ["baseline", "All-time"]].map(([key, lbl]) => (
              <button
                key={key}
                onClick={() => setBaseWin(key)}
                data-testid={`baseline-win-${key}`}
                className={`px-3 py-1 text-[11px] tracking-[0.1em] uppercase font-bold border transition-colors ${baseWin === key ? "bg-foreground text-background border-foreground" : "bg-background text-muted-foreground border-border hover:border-foreground"}`}
              >
                {lbl}
              </button>
            ))}
          </div>
        </div>
        <div className="border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">
                <th className="text-left p-3">Metric</th><th className="text-right p-3">Latest</th>
                <th className="text-right p-3">L7</th><th className="text-right p-3">L30</th>
                <th className="text-right p-3">Base (all-time)</th><th className="text-right p-3">Median</th>
                <th className="text-right p-3">Δ vs {baseWin === "last_7" ? "L7" : baseWin === "l30" ? "L30" : "All-time"}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(a.metrics_comparison).map(([k, v]) => (
                <tr key={k} className="border-b border-border last:border-0" data-testid={`metric-row-${k}`}>
                  <td className="p-3 font-medium">{METRIC_META[k]?.label || k}</td>
                  <td className="p-3 text-right tabular-nums font-bold" data-testid={`metric-latest-${k}`}>{v.latest ?? "-"}{METRIC_META[k]?.unit}</td>
                  <td className="p-3 text-right tabular-nums">{v.last_7 ?? "-"}{METRIC_META[k]?.unit}</td>
                  <td className="p-3 text-right tabular-nums text-muted-foreground">{v.l30 ?? "-"}{METRIC_META[k]?.unit}</td>
                  <td className="p-3 text-right tabular-nums text-muted-foreground" data-testid={`metric-baseline-${k}`}>{v.baseline}{METRIC_META[k]?.unit}</td>
                  <td className="p-3 text-right tabular-nums text-muted-foreground">{v.median ?? "-"}{METRIC_META[k]?.unit}</td>
                  <td className="p-3 text-right" data-testid={`metric-delta-${k}`}><Trend dev={computeDev(v.latest, v[baseWin], v.higher)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="p-8">
        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-5 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Injury & Medical Log {!canMedical && <span className="normal-case tracking-normal font-normal">(restricted · medical staff only)</span>}
        </p>
        {!canMedical ? (
          <div className="border border-border p-6 text-sm text-muted-foreground" data-testid="medical-restricted">
            Access to sensitive medical notes is restricted to Coach/Physio under DPDP Act, 2023.
          </div>
        ) : a.injury_notes.length === 0 ? (
          <div className="border border-border p-6 text-sm text-muted-foreground">No injury notes on record.</div>
        ) : (
          <div className="border border-border divide-y divide-border" data-testid="injury-log">
            {a.injury_notes.map((n, i) => (
              <div key={i} className="p-4 flex items-start gap-4">
                <span className={`text-xs tracking-widest uppercase font-bold px-2 py-1 border ${n.severity === "moderate" ? "border-traffic-red text-traffic-red" : "border-border text-muted-foreground"}`}>{n.severity}</span>
                <div><div className="text-sm">{n.note}</div><div className="text-xs text-muted-foreground mt-1">{n.date}</div></div>
              </div>
            ))}
          </div>
        )}
        {canMedical && (
          <div className="border border-border mt-4 p-4" data-testid="injury-form">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-3 flex items-center gap-2"><Plus className="w-4 h-4" /> Doctor injury input (feeds injury model)</p>
            <div className="grid sm:grid-cols-4 gap-3">
              <input data-testid="inj-bodypart" value={inj.body_part} onChange={(e) => setInj({ ...inj, body_part: e.target.value })} placeholder="Body part" className="inp" />
              <select data-testid="inj-severity" value={inj.severity} onChange={(e) => setInj({ ...inj, severity: e.target.value })} className="inp"><option value="mild">mild</option><option value="moderate">moderate</option><option value="severe">severe</option></select>
              <input data-testid="inj-recovery" type="number" value={inj.recovery_timeline_days} onChange={(e) => setInj({ ...inj, recovery_timeline_days: e.target.value })} placeholder="Recovery days" className="inp" />
              <button data-testid="inj-save" onClick={addInjury} className="bg-foreground text-background px-4 py-2 text-sm hover:bg-neutral-800 transition-colors">Log injury</button>
            </div>
            <input value={inj.notes} onChange={(e) => setInj({ ...inj, notes: e.target.value })} placeholder="Clinical notes / recovery timeline" className="inp mt-3" />
          </div>
        )}
      </div>

      {isStaff && (
        <div className="p-8 border-t border-border">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-5 flex items-center gap-2"><ClipboardList className="w-4 h-4" /> External Assessments (off-plan tests)</p>
          {(a.assessments || []).length > 0 && (
            <div className="border border-border divide-y divide-border mb-4" data-testid="assessment-list">
              {a.assessments.map((x, i) => (
                <div key={i} className="p-3 flex items-center gap-4 text-sm">
                  <span className="font-medium flex-1">{x.name}</span>
                  <span className="font-heading text-lg tabular-nums">{x.value}{x.unit && ` ${x.unit}`}</span>
                  <span className="text-xs text-muted-foreground w-24 text-right">{x.date}</span>
                </div>
              ))}
            </div>
          )}
          <div className="grid sm:grid-cols-5 gap-3" data-testid="assessment-form">
            <input data-testid="asmt-name" value={asmt.name} onChange={(e) => setAsmt({ ...asmt, name: e.target.value })} placeholder="Test name" className="inp sm:col-span-2" />
            <input data-testid="asmt-value" type="number" value={asmt.value} onChange={(e) => setAsmt({ ...asmt, value: e.target.value })} placeholder="Value" className="inp" />
            <input value={asmt.unit} onChange={(e) => setAsmt({ ...asmt, unit: e.target.value })} placeholder="Unit" className="inp" />
            <button data-testid="asmt-save" onClick={addAssessment} className="bg-foreground text-background px-4 py-2 text-sm hover:bg-neutral-800 transition-colors">Log</button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Logged assessments are correlated to score & injury outcomes on the Training page.</p>
        </div>
      )}

      {rtp?.has_injury && (
        <div className="p-8 border-t border-border" data-testid="return-plan">
          <div className="flex items-center gap-3 mb-5 flex-wrap">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground flex items-center gap-2"><HeartPulse className="w-4 h-4" /> Return-to-Play · {rtp.plan.body_part} ({rtp.plan.severity})</p>
            <span className="text-xs border border-border px-2 py-1" data-testid="safe-match-date">Safe match date: <b>{rtp.plan.safe_match_date}</b></span>
            {rtp.plan.match_before_safe && <span className="text-xs border border-traffic-red text-traffic-red px-2 py-1" data-testid="match-warning">⚠ Match day ({rtp.plan.target_date}) is BEFORE safe return</span>}
            {canMedical && <button data-testid="apply-rtp" onClick={applyRtp} className="ml-auto bg-foreground text-background px-4 py-2 text-sm hover:bg-neutral-800 transition-colors">Apply rehab plan to calendar</button>}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border">
            {rtp.plan.phases.map((ph, i) => (
              <div key={i} className="bg-background p-4" data-testid={`rtp-phase-${i}`}>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-muted-foreground">Phase {i + 1}</div>
                <div className="font-heading font-bold tracking-tight mt-1">{ph.name}</div>
                <div className="text-xs text-muted-foreground mt-1 tabular-nums">{ph.start.slice(5)} → {ph.end.slice(5)}</div>
                <div className="text-xs mt-2 leading-snug">{ph.goal}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {isStaff && (
        <div className="p-8 border-t border-border grid lg:grid-cols-2 gap-8">
          <div data-testid="targets-editor">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4 flex items-center gap-2"><Target className="w-4 h-4" /> Training Targets · SNC & Drills</p>
            {["snc", "drills"].map((tc) => (
              <div key={tc} className="border border-border mb-4">
                <div className="px-3 py-2 border-b border-border flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest font-bold">{tc}</span>
                  <div className="flex gap-2">
                    <button data-testid={`add-target-${tc}`} onClick={() => addTarget(tc)} className="text-xs border border-border px-2 py-1 hover:bg-surface transition-colors">+ Add</button>
                    <button data-testid={`save-target-${tc}`} onClick={() => saveTargets(tc)} className="text-xs bg-foreground text-background px-2 py-1 hover:bg-neutral-800 transition-colors">Save</button>
                  </div>
                </div>
                <div className="p-3 space-y-2">
                  {targets[tc].length === 0 && <p className="text-xs text-muted-foreground">No targets set.</p>}
                  {targets[tc].map((t, i) => (
                    <div key={i} className="flex gap-2">
                      <input value={t.name} onChange={(e) => setTargetField(tc, i, "name", e.target.value)} placeholder="Metric" className="inp text-sm flex-1" />
                      <input value={t.target} onChange={(e) => setTargetField(tc, i, "target", e.target.value)} placeholder="Target" className="inp text-sm w-28" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div data-testid="match-log-form">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-4 flex items-center gap-2"><Trophy className="w-4 h-4" /> Log a Match Result</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input type="date" data-testid="match-date" value={match.date} onChange={(e) => setMatch({ ...match, date: e.target.value })} className="inp" />
                <input data-testid="match-event" value={match.event} onChange={(e) => setMatch({ ...match, event: e.target.value })} placeholder="Event / meet" className="inp" />
              </div>
              <input data-testid="match-result" value={match.result} onChange={(e) => setMatch({ ...match, result: e.target.value })} placeholder="Result (e.g. 16.72m, 2nd)" className="inp" />
              <input value={match.condition_note} onChange={(e) => setMatch({ ...match, condition_note: e.target.value })} placeholder="Condition note (optional)" className="inp" />
              <button data-testid="save-match" onClick={addMatch} className="bg-foreground text-background px-4 py-2 text-sm hover:bg-neutral-800 transition-colors">Log match</button>
              <p className="text-xs text-muted-foreground">Match logs & stats appear under Strategy → Match Stats.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b border-border pb-2">
    <dt className="text-muted-foreground">{k}</dt><dd className="font-medium text-right capitalize">{v}</dd>
  </div>
);
